import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getReschedulePolicyWindow,
  formatReschedulePolicyWindowStart,
  formatReschedulePolicyWindowRange,
} from './reschedulePolicyWindow';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RescheduleSlot {
  date: string;
  dayLabel: string;
  startTime: string;
  endTime: string;
}

interface RescheduleSummaryModalProps {
  open: boolean;
  onClose: () => void;
  /** Total minutes of reschedule_only slots in the next 7 days (HOY+1..HOY+7). */
  totalMinutes: number;
  /** Minutes within reschedule_only slots already occupied by booked sessions. */
  reservedMinutes: number;
  /** Free minutes = totalMinutes − reservedMinutes. */
  availableMinutes: number;
  /** Individual reschedule_only blocks to display in the slot list. */
  slots: RescheduleSlot[];
  /** Called when "Agregar horarios de reprogramación" is clicked. */
  onConfigureSchedules: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const REQUIRED_MINUTES = 240; // 4 h

const fmtH = (m: number) =>
  `${Math.floor(m / 60)}h${m % 60 > 0 ? ` ${m % 60}min` : ''}`;

// ─── Sub-component: Stat card ─────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string;
  valueClass?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, valueClass = 'text-[#4a3d73]' }) => (
  <div className="bg-gray-50 rounded-xl px-4 py-3 flex flex-col gap-0.5">
    <span className="text-[0.6875rem] text-gray-500 font-medium leading-snug">
      {label}
    </span>
    <span className={`text-[1rem] font-semibold leading-snug ${valueClass}`}>
      {value}
    </span>
  </div>
);

// ─── Main modal ───────────────────────────────────────────────────────────────

export const RescheduleSummaryModal: React.FC<RescheduleSummaryModalProps> = ({
  open,
  onClose,
  reservedMinutes,
  availableMinutes,
  slots,
  onConfigureSchedules,
}) => {
  const missingMinutes = Math.max(0, REQUIRED_MINUTES - availableMinutes);
  const meetsPolicy = availableMinutes >= REQUIRED_MINUTES;

  const [slotsOpen, setSlotsOpen] = useState(false);
  // Por defecto abierto cuando no se cumple la política (horas faltantes > 0),
  // para que la psicóloga vea de entrada qué franjas tiene disponibles.
  useEffect(() => { if (open) setSlotsOpen(missingMinutes > 0); }, [open, missingMinutes]);

  // Calculado en tiempo real: la ventana real de la política empieza 24hs
  // después del momento de la consulta, no a las 00:00 del día siguiente.
  const policyWindow = getReschedulePolicyWindow();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        data-testid="reschedule-summary-modal"
        className="sm:max-w-[28rem] p-0 overflow-hidden"
      >
        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
          <DialogTitle className="text-[1.0625rem] font-semibold text-[#4a3d73]">
            Tus horarios de reprogramación
          </DialogTitle>
          <p className="text-[0.8125rem] text-gray-500 mt-1 leading-snug">
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <span
                  data-testid="reschedule-summary-period-label"
                  className="underline decoration-dotted underline-offset-2 cursor-help"
                >
                  Próximos 7 días
                </span>
              </TooltipTrigger>
              <TooltipContent data-testid="reschedule-summary-period-tooltip" side="bottom">
                {formatReschedulePolicyWindowRange(policyWindow)}
              </TooltipContent>
            </Tooltip>
            {' '}(desde {formatReschedulePolicyWindowStart(policyWindow)})
          </p>
        </DialogHeader>

        {/* ── Stats grid ── */}
        <div className="px-6 pt-5 pb-3 grid grid-cols-2 gap-3">
          <StatCard label="Horas libres" value={fmtH(availableMinutes)} />
          <StatCard
            label="Horas agendadas"
            value={reservedMinutes > 0 ? fmtH(reservedMinutes) : '—'}
            valueClass="text-gray-600"
          />
          <StatCard
            label="Horas requeridas"
            value="4h"
            valueClass="text-gray-600"
          />
          <StatCard
            label={meetsPolicy ? '¡Cumplís la política!' : 'Horas faltantes'}
            value={meetsPolicy ? '✓' : fmtH(missingMinutes)}
            valueClass={meetsPolicy ? 'text-green-600' : 'text-red-600'}
          />
        </div>

        {/* ── Slot list ── */}
        <div className="px-6 pb-5">
          {slots.length === 0 ? (
            <>
              <p className="text-[0.6875rem] font-semibold text-[#8475b3] uppercase tracking-wider mb-2">
                Franjas del período
              </p>
              <p
                data-testid="reschedule-summary-empty"
                className="text-[0.8125rem] text-gray-400 italic"
              >
                No tenés horarios de reprogramación configurados en este período.
              </p>
            </>
          ) : (
            <>
              <button
                type="button"
                data-testid="reschedule-summary-slots-toggle"
                onClick={() => setSlotsOpen((v) => !v)}
                className="flex items-center justify-between w-full text-left gap-2 mb-1"
              >
                <span className="text-[0.6875rem] font-semibold text-[#8475b3] uppercase tracking-wider">
                  Franjas del período
                </span>
                {slotsOpen
                  ? <ChevronUp className="h-3.5 w-3.5 text-[#8475b3] shrink-0" />
                  : <ChevronDown className="h-3.5 w-3.5 text-[#8475b3] shrink-0" />
                }
              </button>
              {slotsOpen && (
                <div
                  data-testid="reschedule-summary-slots"
                  className="flex flex-col gap-1 max-h-36 overflow-y-auto mt-1"
                >
                  <span
                    data-testid="reschedule-summary-slots-title"
                    className="text-[0.75rem] text-gray-500 mb-0.5"
                  >
                    Tus horarios libres en este período:
                  </span>
                  {slots.map((slot, idx) => (
                    <span
                      key={`${slot.date}-${slot.startTime}-${idx}`}
                      className="text-[0.8125rem] text-gray-600"
                    >
                      {slot.dayLabel} · {slot.startTime}–{slot.endTime}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer transition-colors"
          >
            Cerrar
          </button>
          <button
            type="button"
            data-testid="reschedule-summary-configure-btn"
            onClick={onConfigureSchedules}
            className="px-5 py-2 rounded-full text-sm font-semibold text-white border-none cursor-pointer transition-all hover:brightness-90"
            style={{ background: '#6D57BC' }}
          >
            Agregar horarios de reprogramación
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleSummaryModal;
